import {
  ColorVariant,
  InventoryVariant,
  PriceVariant,
  Variant,
} from "~/entities/variant/types";

export function isVariantTypesafe(variant: unknown): Variant | null {
  if (!variant || typeof variant !== "object") {
    return null;
  }

  const v = variant as Record<string, unknown>;

  // Check required primitive properties
  if (
    typeof v.id !== "string" ||
    typeof v.color !== "string" ||
    typeof v.size !== "string" ||
    typeof v.sku !== "string" ||
    typeof v.isCloseout !== "boolean" ||
    typeof v.isDropship !== "boolean"
  ) {
    return null;
  }

  // If all checks pass, return the variant
  return variant as Variant;
}

export function isColorVariantTypesafe(variant: unknown): ColorVariant | null {
  try {
    if (!variant || typeof variant !== "object") {
      throw new Error("Color variant must be a non-null object");
    }

    const v = variant as Record<string, unknown>;

    // Check required primitive properties
    if (
      typeof v.color !== "string" ||
      typeof v.colorCode !== "string" ||
      typeof v.startingPrice !== "number" ||
      typeof v.isCloseout !== "boolean" ||
      typeof v.isDropship !== "boolean"
    ) {
      throw new Error(
        "Color variant must have valid color, colorCode, startingPrice, isCloseout, and isDropship properties",
      );
    }

    // Check imageUrls object
    if (!v.imageUrls || typeof v.imageUrls !== "object") {
      throw new Error("Color variant must have a valid imageUrls object");
    }

    const imageUrls = v.imageUrls as Record<string, unknown>;
    if (
      (imageUrls.front && typeof imageUrls.front !== "object") ||
      (imageUrls.side && typeof imageUrls.side !== "object") ||
      (imageUrls.back && typeof imageUrls.back !== "object")
    ) {
      throw new Error(
        "Color variant imageUrls must have valid front, side, and back objects",
      );
    }

    // Check swatch object
    if (!v.swatch || typeof v.swatch !== "object") {
      throw new Error("Color variant must have a valid swatch object");
    }

    const swatch = v.swatch as Record<string, unknown>;
    if (
      !Array.isArray(swatch.hexCodes) ||
      !swatch.hexCodes.every((code: unknown) => typeof code === "string") ||
      (swatch.url !== null && typeof swatch.url !== "string")
    ) {
      throw new Error(
        "Color variant swatch must have valid hexCodes array and url property",
      );
    }

    // If all checks pass, return the variant
    return variant as ColorVariant;
  } catch (error) {
    if (error instanceof Error) {
      console.error(error.message);
    } else {
      console.error("An unknown error occurred while validating color variant");
    }
    return null;
  }
}

export function isInventoryVariantTypesafe(
  variant: unknown,
): InventoryVariant | null {
  try {
    if (!variant || typeof variant !== "object") {
      throw new Error("Inventory variant must be a non-null object");
    }

    const v = variant as Record<string, unknown>;

    // Check required primitive properties
    if (typeof v.id !== "string" || typeof v.inventory !== "number") {
      throw new Error(
        "Inventory variant must have valid id string and inventory number",
      );
    }

    // If all checks pass, return the variant
    return variant as InventoryVariant;
  } catch (error) {
    if (error instanceof Error) {
      console.error(error.message);
    } else {
      console.error(
        "An unknown error occurred while validating inventory variant",
      );
    }
    return null;
  }
}

export function isPriceVariantTypesafe(variant: unknown): PriceVariant | null {
  try {
    if (!variant || typeof variant !== "object") {
      throw new Error("Price variant must be a non-null object");
    }

    const v = variant as Record<string, unknown>;

    // Check required primitive properties
    if (
      typeof v.id !== "string" ||
      typeof v.price !== "number" ||
      typeof v.onSale !== "boolean"
    ) {
      throw new Error(
        "Price variant must have valid id string, price number, and onSale boolean",
      );
    }

    // If all checks pass, return the variant
    return variant as PriceVariant;
  } catch (error) {
    if (error instanceof Error) {
      console.error(error.message);
    } else {
      console.error("An unknown error occurred while validating price variant");
    }
    return null;
  }
}
